/**
 * @fileoverview Generic sorted list of single-color proportional bars.
 * @description Each row is a label, a horizontal bar sized relative to either the largest item or
 * the group's total, and a right-aligned value in a fixed-width column so every row's value lines
 * up regardless of how wide that row's bar is. Ported from the InsightBox Banking design system's
 * own `RankedHBar`, restyled onto this app's own tokens. Confirmed structurally distinct from the
 * existing `SegmentedBar` (one bar split into colored segments) — this is N separate full-width
 * bars, one per row.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { CHART_PRIMARY } from '@/shared/constants/chart-theme.constants'

export interface RankedBarListItem {
  key: string
  label: string
  value: number
  displayValue: string
  color?: string
}

interface RankedBarListProps {
  items: RankedBarListItem[]
  labelWidth?: number
  scaleBy?: 'max' | 'total'
}

/**
 * @component
 * @param {RankedBarListProps} props - The rows to render and their scaling/label-width options.
 * @param {RankedBarListItem[]} props.items - The rows, in the order they should render (the
 * caller is responsible for any sorting).
 * @param {number} [props.labelWidth] - Fixed pixel width of the label column. Defaults to 160.
 * @param {'max' | 'total'} [props.scaleBy] - "max" (default): each bar's width is relative to the
 * largest item — right for ranking independent metrics (e.g. NPA % per sector) against each
 * other. "total": each bar's width is its true share of the sum of all items — right for a
 * composition that adds up to a whole (e.g. an investment mix), so an 89% item actually looks
 * like ~89% of the track, not stretched out to fill it.
 */
function RankedBarList({ items, labelWidth = 160, scaleBy = 'max' }: RankedBarListProps) {
  const denom =
    scaleBy === 'total'
      ? Math.max(
          items.reduce((sum, item) => sum + item.value, 0),
          1,
        )
      : Math.max(...items.map((item) => item.value), 1)

  return (
    <div className="flex h-full flex-col justify-around gap-2">
      {items.map((item) => {
        const pct = Math.max((item.value / denom) * 100, 1.5)
        const color = item.color ?? CHART_PRIMARY
        return (
          <div key={item.key} className="flex items-center gap-3">
            <p
              className="shrink-0 truncate text-sm font-medium text-text"
              style={{ width: labelWidth }}
              title={item.label}
            >
              {item.label}
            </p>
            <div className="h-6 flex-1 rounded-md bg-input-bg">
              <div
                className="h-full rounded-md transition-all"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
            {/* Always outside the bar, in a fixed-width column, so every row's value lines up in
                the same place regardless of how wide that row's bar happens to be. */}
            <span className="w-[132px] shrink-0 text-right text-[11px] font-semibold whitespace-nowrap text-text">
              {item.displayValue}
            </span>
          </div>
        )
      })}
    </div>
  )
}

export default RankedBarList
